import { IconButton } from '@synoptic/ui-kit/button/icon-button.js';
import { Checkbox } from '@synoptic/ui-kit/checkbox/checkbox.js';
import { ArrowLeftIcon } from '@synoptic/ui-kit/icons/react/arrow-left.js';
import {
  ModalRoot,
  ModalContent,
  ModalHeader,
  ModalTitle,
  ModalTrigger,
} from '@synoptic/ui-kit/modal/modal.js';
import clsx from 'clsx';
import { PropsWithChildren, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { AiFilterInput } from '@/__generated__/graphql';
import { useMe } from '@/routes/_app/use-me';
import { usePersistState } from '@/state-utils/persisted-state';
import { AiFilterFormValues, AiFilter } from '../types';
import { AIFilterForm } from './ai-filter-form';
import { SaveFilterForm } from './save-filter-form';
import { backButton } from '../ai-filters.css';
import {
  footerCheckbox,
  footerCheckboxText,
  manageFilterContainer,
  manageFilterModal,
} from './manage-filter.css';

export interface ManageFilterModalProps {
  filter?: AiFilter | null;
  onSubmit: (data: AiFilterInput) => void;
}

enum ManageFilterStep {
  Details = 'details',
  Name = 'name',
}

export const ManageFilterModal = ({
  children,
  filter,
  ...props
}: PropsWithChildren<ManageFilterModalProps>) => {
  const [isOpen, setIsOpen] = useState(false);
  const [step, setStep] = useState<ManageFilterStep>(ManageFilterStep.Details);

  const [isConfirmLeaveModalOpen, setIsConfirmLeaveModalOpen] = useState(false);
  const [isConfirmedLeave, setIsConfirmedLeave] = useState(false);
  const [shouldShowConfirmLeaveModal, setShouldShowConfirmLeaveModal] =
    useState(false);
  const isEdit = !!filter;

  const { protectedFields } = useMe();

  const resetModal = () => {
    setIsConfirmedLeave(false);
    setIsConfirmLeaveModalOpen(false);
    setStep(ManageFilterStep.Details);
  };

  const showConfirmLeaveModal = () => {
    if (shouldShowConfirmLeaveModal) {
      setIsConfirmLeaveModalOpen(true);
    } else {
      setIsOpen(false);
    }
  };

  return (
    <>
      <ModalRoot
        open={isOpen}
        onOpenChange={(shouldOpen) => {
          if (!shouldOpen && step === ManageFilterStep.Name) {
            setStep(ManageFilterStep.Details);
            return;
          }
          if (
            !shouldOpen &&
            !isConfirmedLeave &&
            step === ManageFilterStep.Details
          ) {
            showConfirmLeaveModal();
            return;
          }
          setIsOpen(shouldOpen);
          resetModal();
        }}
      >
        <ModalTrigger
          disabled={!protectedFields?.openAIKey?.availableModels}
          asChild
        >
          {children}
        </ModalTrigger>
        <ModalContent
          className={clsx({
            [manageFilterModal]: step === ManageFilterStep.Details,
          })}
        >
          <ModalHeader withoutCloseIcon>
            <IconButton
              onClick={() => {
                if (step === ManageFilterStep.Name) {
                  setStep(ManageFilterStep.Details);
                } else {
                  showConfirmLeaveModal();
                }
              }}
              className={backButton}
              variant="ghost"
              size="small"
            >
              <ArrowLeftIcon style={{ width: 24, height: 24 }} />
            </IconButton>
            <ModalTitle>
              {step === ManageFilterStep.Details &&
                (isEdit
                  ? `Edit ${filter?.name} Filter`
                  : 'Create new AI Filter')}
              {step === ManageFilterStep.Name && 'Save filter'}
            </ModalTitle>
          </ModalHeader>
          {!!protectedFields?.openAIKey?.availableModels && (
            <ModalFormContainer
              setIsOpen={setIsOpen}
              step={step}
              setStep={setStep}
              isEdit={isEdit}
              filter={filter}
              onSubmit={props.onSubmit}
              showConfirmLeaveModal={showConfirmLeaveModal}
              setShouldShowConfirmLeaveModal={setShouldShowConfirmLeaveModal}
              availableModels={protectedFields.openAIKey.availableModels}
              isConfirmLeaveModalOpen={isConfirmLeaveModalOpen}
              setIsConfirmLeaveModalOpen={setIsConfirmLeaveModalOpen}
              onConfirmLeave={() => {
                setIsConfirmedLeave(true);
                setIsConfirmLeaveModalOpen(false);
                setIsOpen(false);
              }}
            />
          )}
        </ModalContent>
      </ModalRoot>
    </>
  );
};

enum PolicyState {
  Accepted = 'accepted',
  Rejected = 'rejected',
}

export const ModalFormContainer = ({
  filter,
  setIsOpen,
  onSubmit,
  step,
  setStep,
  isEdit,
  showConfirmLeaveModal,
  setShouldShowConfirmLeaveModal,
  availableModels,
  isConfirmLeaveModalOpen,
  setIsConfirmLeaveModalOpen,
  onConfirmLeave,
}: ManageFilterModalProps & {
  setIsOpen: (isOpen: boolean) => void;
  step: ManageFilterStep;
  setStep: (step: ManageFilterStep) => void;
  showConfirmLeaveModal: () => void;
  isEdit: boolean;
  setShouldShowConfirmLeaveModal: (shouldShow: boolean) => void;
  availableModels: string[];
  isConfirmLeaveModalOpen: boolean;
  setIsConfirmLeaveModalOpen: (isOpen: boolean) => void;
  onConfirmLeave: () => void;
}) => {
  const {
    state: hasAcceptedPolicy,
    setState: setHasAcceptedPolicy,
    initialState: initialHasAcceptedPolicy,
  } = usePersistState({
    key: 'hasAcceptedPolicy',
    states: PolicyState,
    defaultState: PolicyState.Rejected,
  });

  const [detailsStepData, setDetailsStepData] = useState<AiFilterFormValues>({
    prompt: filter?.prompt ?? '',
    model: filter?.model ?? availableModels?.[0] ?? '',
    passValue: filter?.passValue ?? null,
  });

  const formData = useForm<AiFilterFormValues>({
    defaultValues: detailsStepData,
    shouldFocusError: true,
  });

  useEffect(() => {
    setShouldShowConfirmLeaveModal(
      !!Object.keys(formData.formState.touchedFields).length,
    );
  }, [formData.formState.touchedFields, setShouldShowConfirmLeaveModal]);

  const availableModelsSelectOptions = availableModels.map((model) => ({
    content: model,
    value: model,
  }));

  return (
    <>
      <div className={manageFilterContainer}>
        {step === ManageFilterStep.Details && (
          <>
            <AIFilterForm
              onCancel={showConfirmLeaveModal}
              hasAcceptedPolicy={hasAcceptedPolicy === PolicyState.Accepted}
              isEdit={isEdit}
              formData={formData}
              modelSelectOptions={availableModelsSelectOptions ?? []}
              onSubmit={(data) => {
                setDetailsStepData(data);
                setStep(ManageFilterStep.Name);
              }}
              onConfirmLeave={onConfirmLeave}
              isConfirmLeaveModalOpen={isConfirmLeaveModalOpen}
              setIsConfirmLeaveModalOpen={setIsConfirmLeaveModalOpen}
            >
              {initialHasAcceptedPolicy === PolicyState.Accepted ? (
                <p className={footerCheckboxText}>
                  Continuing with applying filter you agree that you are
                  responsible for the cost of AI usage.
                </p>
              ) : (
                <label className={footerCheckbox}>
                  <Checkbox
                    checked={hasAcceptedPolicy === PolicyState.Accepted}
                    onCheckedChange={(checked) =>
                      setHasAcceptedPolicy(
                        checked ? PolicyState.Accepted : PolicyState.Rejected,
                      )
                    }
                  />
                  I understand that I am responsible for the cost of AI usage
                </label>
              )}
            </AIFilterForm>
          </>
        )}
        {step === ManageFilterStep.Name && (
          <SaveFilterForm
            name={filter?.name}
            onCancel={() => setStep(ManageFilterStep.Details)}
            onSubmit={async ({ name }) => {
              const updatedFilterData = {
                name,
                prompt: detailsStepData.prompt,
                model: detailsStepData.model,
                passValue: detailsStepData.passValue!,
              };
              await onSubmit(updatedFilterData);
              setIsOpen(false);
            }}
          />
        )}
      </div>
    </>
  );
};
